/**
 * "HTML / Word" section: independent, dependency-free validation and rendering
 * of the stored JSON document. HTML is never accepted here: only a closed set of
 * node types, marks and attributes. Any deviation fails closed (TypeError).
 * Limits mirror packages/shared/src/cms/html-doc.ts (parity-tested).
 */
export const HTML_DOC_LIMITS = Object.freeze({
  maxSourceChars: 262_144,
  maxCharacters: 10_000,
  maxNodes: 1_500,
  maxDepth: 10,
  maxRows: 40,
  maxColumns: 10,
  maxSerialized: 60_000,
  maxStart: 9_999,
});

const INLINE = ['text', 'hardBreak'];
const CHILDREN = {
  doc: ['paragraph', 'heading', 'bulletList', 'orderedList', 'table'],
  paragraph: INLINE,
  heading: INLINE,
  bulletList: ['listItem'],
  orderedList: ['listItem'],
  listItem: ['paragraph', 'bulletList', 'orderedList'],
  table: ['tableRow'],
  tableRow: ['tableHeader', 'tableCell'],
  tableHeader: ['paragraph'],
  tableCell: ['paragraph'],
  text: [],
  hardBreak: [],
};
/** Containers that must not be empty to stay meaningful and editable. */
const NON_EMPTY = new Set(['bulletList', 'orderedList', 'listItem', 'table', 'tableRow', 'tableHeader', 'tableCell']);

function fail(reason) {
  throw new TypeError(`Invalid CMS HTML document: ${reason}`);
}
function plain(value, reason) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) fail(reason);
  return value;
}
function onlyKeys(value, keys, reason) {
  for (const key of Object.keys(value)) if (!keys.includes(key)) fail(`${reason}: unexpected "${key}"`);
}
function hasControl(value) {
  return [...value].some(char => { const n = char.charCodeAt(0); return (n < 32 && n !== 9) || n === 127; });
}

/**
 * Validate and return a canonical copy. `link(href)` must return the accepted
 * destination or throw (publication: validateLink; preview: escaping only).
 */
export function validateHtmlDoc(value, link) {
  let nodes = 0;
  let characters = 0;
  function visit(raw, depth, parent) {
    const node = plain(raw, 'node object');
    const type = node.type;
    if (typeof type !== 'string' || !Object.hasOwn(CHILDREN, type)) fail('unknown node');
    if (parent ? !CHILDREN[parent].includes(type) : type !== 'doc') fail(`"${type}" not allowed in "${parent ?? 'root'}"`);
    if (depth > HTML_DOC_LIMITS.maxDepth) fail('too deep');
    if (++nodes > HTML_DOC_LIMITS.maxNodes) fail('too many nodes');
    if (type === 'text') {
      onlyKeys(node, ['type', 'text', 'marks'], 'text');
      if (typeof node.text !== 'string' || !node.text || hasControl(node.text)) fail('text value');
      characters += node.text.length;
      if (characters > HTML_DOC_LIMITS.maxCharacters) fail('too many characters');
      const result = { type, text: node.text };
      if (node.marks !== undefined) {
        if (!Array.isArray(node.marks) || !node.marks.length || node.marks.length > 3) fail('marks');
        const seen = new Set();
        result.marks = node.marks.map(rawMark => {
          const mark = plain(rawMark, 'mark');
          if (seen.has(mark.type)) fail('duplicate mark');
          seen.add(mark.type);
          if (mark.type === 'bold' || mark.type === 'italic') {
            onlyKeys(mark, ['type'], 'mark');
            return { type: mark.type };
          }
          if (mark.type === 'link') {
            onlyKeys(mark, ['type', 'attrs'], 'link');
            const attrs = plain(mark.attrs, 'link attrs');
            onlyKeys(attrs, ['href'], 'link attrs');
            if (typeof attrs.href !== 'string') fail('link href');
            return { type: 'link', attrs: { href: link(attrs.href) } };
          }
          return fail('unknown mark');
        });
      }
      return result;
    }
    if (type === 'hardBreak') {
      onlyKeys(node, ['type'], 'hardBreak');
      return { type };
    }
    const result = { type };
    if (type === 'heading') {
      onlyKeys(node, ['type', 'attrs', 'content'], type);
      const attrs = plain(node.attrs, 'heading attrs');
      onlyKeys(attrs, ['level'], 'heading attrs');
      if (attrs.level !== 2 && attrs.level !== 3) fail('heading level');
      result.attrs = { level: attrs.level };
    } else if (type === 'orderedList') {
      onlyKeys(node, ['type', 'attrs', 'content'], type);
      if (node.attrs !== undefined) {
        const attrs = plain(node.attrs, 'list attrs');
        onlyKeys(attrs, ['start'], 'list attrs');
        if (!Number.isSafeInteger(attrs.start) || attrs.start < 1 || attrs.start > HTML_DOC_LIMITS.maxStart) fail('list start');
        result.attrs = { start: attrs.start };
      }
    } else onlyKeys(node, ['type', 'content'], type);
    if (node.content !== undefined && !Array.isArray(node.content)) fail('content');
    const content = node.content ?? [];
    if (NON_EMPTY.has(type) && !content.length) fail(`empty ${type}`);
    if (type === 'listItem' && plain(content[0], 'list item').type !== 'paragraph') fail('list item must start with a paragraph');
    if (type === 'table') {
      if (content.length > HTML_DOC_LIMITS.maxRows) fail('too many rows');
      const widths = new Set(content.map(row => (Array.isArray(plain(row, 'row').content) ? row.content.length : 0)));
      const [width] = widths;
      if (widths.size !== 1 || !width || width > HTML_DOC_LIMITS.maxColumns) fail('table must be rectangular, max 10 columns');
    }
    if (content.length) result.content = content.map(child => visit(child, depth + 1, type));
    return result;
  }
  const doc = visit(value, 0);
  if (JSON.stringify(doc).length > HTML_DOC_LIMITS.maxSerialized) fail('document too large');
  return doc;
}

export function htmlDocHasText(node) {
  return node?.type === 'text' ? Boolean(node.text?.trim()) : (node?.content ?? []).some(htmlDocHasText);
}

/**
 * Whitelisted HTML. `escape` escapes text; `link(href)` returns an escaped,
 * validated destination (preview links are inert about:blank URLs).
 */
export function renderHtmlDoc(doc, escape, link) {
  const inline = nodes => (nodes ?? []).map(node => {
    if (node.type === 'hardBreak') return '<br>';
    if (node.type !== 'text') return '';
    let html = escape(node.text);
    const marks = node.marks ?? [];
    if (marks.some(m => m.type === 'bold')) html = `<strong>${html}</strong>`;
    if (marks.some(m => m.type === 'italic')) html = `<em>${html}</em>`;
    const anchor = marks.find(m => m.type === 'link');
    if (anchor) html = `<a href="${link(anchor.attrs?.href)}">${html}</a>`;
    return html;
  }).join('');
  const cell = (node, header, firstRow) => {
    const paragraphs = node.content ?? [];
    const body = paragraphs.length === 1 ? inline(paragraphs[0].content) : paragraphs.map(p => `<p>${inline(p.content)}</p>`).join('');
    return header ? `<th scope="${firstRow ? 'col' : 'row'}">${body}</th>` : `<td>${body}</td>`;
  };
  const table = node => {
    const rows = node.content ?? [];
    const headRow = rows[0]?.content?.every(c => c.type === 'tableHeader') && rows.length > 1;
    const row = (r, first) => `<tr>${(r.content ?? []).map(c => cell(c, c.type === 'tableHeader', first)).join('')}</tr>`;
    const head = headRow ? `<thead>${row(rows[0], true)}</thead>` : '';
    const bodyRows = (headRow ? rows.slice(1) : rows).map(r => row(r, false)).join('');
    return `<div class="cms-html-table" role="region" aria-label="Tableau" tabindex="0"><table>${head}<tbody>${bodyRows}</tbody></table></div>`;
  };
  const block = node => {
    switch (node.type) {
      case 'paragraph': return `<p>${inline(node.content)}</p>`;
      case 'heading': { const level = node.attrs?.level === 3 ? 3 : 2; return `<h${level}>${inline(node.content)}</h${level}>`; }
      case 'bulletList': return `<ul>${(node.content ?? []).map(block).join('')}</ul>`;
      case 'orderedList': {
        const start = Number.isSafeInteger(node.attrs?.start) && node.attrs.start > 1 ? ` start="${node.attrs.start}"` : '';
        return `<ol${start}>${(node.content ?? []).map(block).join('')}</ol>`;
      }
      case 'listItem': return `<li>${(node.content ?? []).map(block).join('')}</li>`;
      case 'table': return table(node);
      default: return '';
    }
  };
  return `<div class="cms-html-content">${(doc?.content ?? []).map(block).join('')}</div>`;
}
